import React, { useState, useEffect } from 'react';
import axios from 'axios';

const HOASetupSignoffChecklist = ({ hoaData, onComplete }) => {
  const [checklistState, setChecklistState] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const hoaId = hoaData?.hoa?.id || hoaData?.id;

  // Fetch current checklist state and user info
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get current user
        const userResponse = await axios.get('/api/auth/me');
        setCurrentUser(userResponse.data.user);

        // Get checklist state
        const checklistResponse = await axios.get(`/api/hoa/${hoaId}/setup-checklist`);
        if (checklistResponse.data.success) {
          setChecklistState(checklistResponse.data.checklist || {});
        }
      } catch (error) {
        console.error('Failed to load checklist:', error);
      } finally {
        setLoading(false);
      }
    };

    if (hoaId) {
      fetchData();
    }
  }, [hoaId]);

  const checklistItems = [
    {
      id: 'community-branding',
      section: 'Getting Started',
      title: 'Community Branding',
      description: 'Upload your HOA logo and customize colors/theme',
      instructions: 'Go to Settings → Community Branding page to upload logo and select theme colors',
      urgent: true
    },
    {
      id: 'board-members',
      section: 'Getting Started',
      title: 'Add Board Members',
      description: 'Invite or import board members to the system',
      instructions: 'Go to Settings → User Management → Homeowners tab, then use "Invite Homeowners" or "Import CSV" to add board members',
      urgent: true
    },
    {
      id: 'homeowner-import',
      section: 'Getting Started',
      title: 'Import Homeowners',
      description: 'Import all homeowners using CSV upload',
      instructions: (
        <div>
          <p>Go to Settings → User Management → Homeowners tab and click "Import CSV" to bulk upload homeowners</p>
          <div className="mt-2 p-2 bg-orange-50 border-l-4 border-orange-400 text-orange-800">
            <p className="text-sm">
              <strong>💡 Tip:</strong> If you do not have everyone's emails and will allow members to register themselves, create a registration code for them to setup their own accounts in Settings → Registration Codes
            </p>
          </div>
        </div>
      ),
      urgent: true
    },
    {
      id: 'upload-documents',
      section: 'Documents',
      title: 'Upload Governing Documents',
      description: 'Upload CC&Rs, Bylaws, and Rules & Regulations',
      instructions: 'Go to Documents tab and upload your CC&Rs, Bylaws, and Rules & Regulations documents. After uploading your governing documents, go to HOA Smart Pro sub-tab in Documents and click on re-index – this will smartly answer questions for homeowners.',
      urgent: hoaData?.has_cc_rs || hoaData?.has_bylaws
    },
    {
      id: 'payment-setup',
      section: 'Financial',
      title: 'Setup Payment Processing',
      description: 'Configure Stripe for dues and fine collection',
      instructions: 'Go to Settings → Payments & Finance to connect your Stripe account',
      urgent: true
    },
    {
      id: 'budget-setup',
      section: 'Financial',
      title: 'Setup Budget & Tracking',
      description: 'Configure budget to start tracking budget and actual transactions',
      instructions: 'Go to Budget tab and setup budget to start tracking budget and actual transactions',
      urgent: true
    },
    {
      id: 'dues-schedule',
      section: 'Financial',
      title: 'Configure Dues Schedule',
      description: 'Set up monthly/quarterly/annual dues amounts',
      instructions: 'Go to Payments → Budget & Finance to configure your dues schedule and amounts',
      urgent: true
    },
    {
      id: 'neighborhood-setup',
      section: 'Community Features',
      title: 'Setup Neighborhood Tab',
      description: 'Configure community services so neighbors know options for utilities and HOA contact info',
      instructions: 'Setup neighborhood tab by configuring Settings → Community Services so your neighbors know options for utilities and information about contacting the HOA',
      urgent: false
    },
    {
      id: 'public-page-setup',
      section: 'Community Features',
      title: 'Setup Public Page',
      description: 'Configure public-facing page that requires no login',
      instructions: 'Setup the public page in Settings so you have a public facing no-login needed page',
      urgent: false
    },
    {
      id: 'committee-setup',
      section: 'Community Features',
      title: 'Setup Committees',
      description: 'Create committees and add members',
      instructions: 'Setup committees and add members in Settings → Committee Management',
      urgent: false
    }
  ];

  const toggleTask = async (taskId) => {
    setSaving(true);
    try {
      const currentState = checklistState[taskId];
      const newState = !currentState?.completed;

      const response = await axios.post(`/api/hoa/${hoaId}/setup-checklist/${taskId}`, {
        completed: newState
      });

      if (response.data.success) {
        setChecklistState(prev => ({
          ...prev,
          [taskId]: response.data.task_state
        }));
      }
    } catch (error) {
      console.error('Failed to update task:', error);
      alert('Failed to update checklist. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getProgress = () => {
    const total = checklistItems.length;
    const completed = checklistItems.filter(item => checklistState[item.id]?.completed).length;
    return { total, completed, percentage: Math.round((completed / total) * 100) };
  };

  const groupedTasks = checklistItems.reduce((acc, task) => {
    if (!acc[task.section]) {
      acc[task.section] = [];
    }
    acc[task.section].push(task);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading setup checklist...</p>
        </div>
      </div>
    );
  }

  const progress = getProgress();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">HOA Setup Checklist</h1>
              <p className="text-gray-600 mt-1">Complete these tasks to finish setting up your community</p>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium"
            >
              Back to Dashboard
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progress: {progress.completed} of {progress.total} tasks completed
              </span>
              <span className="text-sm font-medium text-indigo-600">
                {progress.percentage}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-indigo-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${progress.percentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>How this works:</strong> This checklist guides you to the right places in your dashboard to configure settings. 
                Complete each task in the appropriate section, then return here to check it off. All board members can see the progress.
              </p>
            </div>
          </div>
        </div>

        {/* Checklist Sections */}
        {Object.entries(groupedTasks).map(([sectionName, tasks]) => (
          <div key={sectionName} className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">{tasks[0].section === 'Getting Started' ? '🚀' : tasks[0].section === 'Documents' ? '📄' : tasks[0].section === 'Financial' ? '💰' : '⚙️'}</span>
              {sectionName}
            </h2>

            <div className="space-y-4">
              {tasks.map(task => {
                const taskState = checklistState[task.id];
                const isCompleted = taskState?.completed || false;

                return (
                  <div
                    key={task.id}
                    className={`border rounded-lg p-4 transition-all ${
                      isCompleted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 pt-1">
                        <input
                          type="checkbox"
                          checked={isCompleted}
                          onChange={() => toggleTask(task.id)}
                          disabled={saving}
                          className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                        />
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className={`text-lg font-semibold ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                            {task.title}
                            {task.urgent && !isCompleted && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                Urgent
                              </span>
                            )}
                          </h3>
                          {isCompleted && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                              ✓ Complete
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                        
                        <div className="mt-3 bg-blue-50 border-l-2 border-blue-400 p-3 rounded">
                          <p className="text-sm text-gray-700">
                            <strong className="text-blue-700">Where to do this:</strong> {task.instructions}
                          </p>
                        </div>

                        {taskState?.completed_by && (
                          <div className="mt-3 text-xs text-gray-500">
                            Completed by <strong>{taskState.completed_by_name}</strong> on{' '}
                            {new Date(taskState.completed_at).toLocaleDateString()} at{' '}
                            {new Date(taskState.completed_at).toLocaleTimeString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Completion Banner */}
        {progress.percentage === 100 && (
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">
                  <strong>Congratulations!</strong> You've completed all setup tasks. Your HOA portal is ready to use!
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HOASetupSignoffChecklist;
